import type { TemplateResult } from 'lit';
import type { IMessage } from '../types/network';

export type HTMLTemplate = TemplateResult<1>;
type TEventHooks = Map<string, Function[]>;

/**
 * Class for adding before and after hooks to methods
 */
export class PointCutFacade {
  /**
   * list of before event hooks for methods
   */
  readonly before: TEventHooks = new Map();

  /**
   * list of after event hooks for methods
   */
  readonly after: TEventHooks = new Map();

  /**
   * to intercept a method and execute render a template
   *
   * @param args
   * @param methodName
   * @param callback
   * @returns
   */
  render<ARGS>(
    template: (args: ARGS) => HTMLTemplate | Symbol,
    args: ARGS,
    methodName: 'render-message-block',
    callback: (args: ARGS, template: (args: ARGS) => HTMLTemplate | Symbol) => HTMLTemplate | Symbol
  ) {
    const beforeOutput = this.__runFnsSync(this.before, methodName, args) as ARGS;
    const afterFns = this.after.get(methodName) ?? [];

    return afterFns.length <= 0
      ? callback(beforeOutput, template)
      : afterFns.reduce((template, fn) => fn(template, beforeOutput), template);
  }

  /**
   * @deprecated use PointcutFacade#invokeSync
   */
  invoke(args: unknown, methodName: string, cb: Function): IMessage {
    return this.invokeSync(args, methodName, cb);
  }

  /**
   * to intercept a method and execute list of functions before and after it
   *
   * @param args
   * @param methodName
   * @param cb
   * @returns
   */
  invokeSync(args: unknown, methodName: string, cb: Function): IMessage {
    const beforeOutput = this.__runFnsSync(this.before, methodName, args);
    const output = cb(beforeOutput);
    const afterOutput = this.__runFnsSync(this.after, methodName, output);

    return afterOutput as IMessage;
  }

  /**
   * to intercept a method and execute list of functions before and after it
   *
   * @param args
   * @param methodName
   * @param cb
   * @returns
   */
  async invokeAsync(args: unknown, methodName: string, cb: Function): Promise<IMessage> {
    const beforeOutput = await this.__runFnsAsync(this.before, methodName, args);
    const output = await cb(beforeOutput);
    const afterOutput = await this.__runFnsAsync(this.after, methodName, output);

    return afterOutput as IMessage;
  }

  /**
   * To run functions and overlap arguments with each successive run
   *
   * @param hook the before or after function
   * @param name of the method
   * @param args arguments to the method
   * @returns args
   */
  private __runFnsSync(hooks: TEventHooks, name: string, args: unknown) {
    return (hooks.get(name) ?? []).reduce((args, fn) => fn(args) ?? args, args);
  }

  /**
   * To run functions and overlap arguments with each successive run
   *
   * @param hook the before or after function
   * @param name of the method
   * @param args arguments to the method
   * @returns args
   */
  private __runFnsAsync(hooks: TEventHooks, name: string, args: unknown) {
    return (hooks.get(name) ?? []).reduce(
      (p, fn) => p.then((args) => fn(args) ?? Promise.resolve(args)),
      Promise.resolve(args)
    );
  }

  /**
   * To add before and after hooks to a methods
   *
   * @param type of hook before or after
   * @param name of the method
   * @param fn function to run before or after the method
   * @returns fn
   */
  add(type: 'before' | 'after', name: string, fn: Function) {
    const hooks = type === 'before' ? this.before : this.after;
    const list = hooks.get(name) ?? [];

    list.push(fn);
    hooks.set(name, list);
    return this;
  }
}
